/**
 * Live data: the one way documents get into the stores.
 *
 * The stores are the app's only cache, and every entry in them is kept
 * current by a Firestore listener (docs/LiveData.md). Store code that needs
 * a document or a query acquires a live source here. The first holder starts
 * the listener and later ones share it. Two minutes after the last holder
 * lets go, the listener stops and the documents that only it supplied leave
 * the store, so nothing in a store can go stale. One-time reads
 * (getDoc/getDocs) may feed a calculation, never a store.
 *
 * Who holds a source decides how long it runs:
 * - `{ session: group }`: until releaseLiveSession(group), or sign-out
 *   (resetLive). The signed-in member's own data, and the admin live set.
 * - 'auto' (the default): the component (or Nuxt app) scope that asked,
 *   else the live source whose snapshot is being applied (a member's
 *   SubKrewe lives as long as the member does), else the current route,
 *   else 'transient'.
 * - 'transient': released as soon as it's ready; the linger keeps it live a
 *   little longer.
 *
 * A source is ready once a snapshot the server confirmed is in the store.
 * Firestore answers a new listener from its cache first when the cache holds
 * any matching document (an admin's own record, for the listener on every
 * member), so that first snapshot can be a fraction of the result. Listeners
 * take their callbacks from liveDocListener/liveQueryListener, which mark
 * those.
 */
import type { DocumentSnapshot, QuerySnapshot } from 'firebase/firestore'
import { getCurrentScope, onScopeDispose } from 'vue'

export interface LiveChange<T = unknown> {
  path: string
  /** The document's data, or null when it no longer exists or left the query */
  data: T | null
}

/**
 * Hands a snapshot's changes to the registry. `fromCache`: Firestore had only
 * its local cache to go on, so the snapshot may be incomplete.
 */
export type LiveEmit<T = unknown> = (changes: LiveChange<T>[], fromCache?: boolean) => void

export interface LiveSpec<T = unknown> {
  /** Identifies the listener: holders with the same key share one */
  key: string
  /** Starts the listener and returns its unsubscribe (liveDocListener, liveQueryListener) */
  listen: (emit: LiveEmit<T>, fail: (error: unknown) => void) => () => void
  /** Puts changed documents into the store (convert, then $patch) */
  apply: (changes: LiveChange<T>[]) => void | Promise<void>
  /** Removes documents that no live source supplies any more */
  evict: (paths: string[]) => void
  /**
   * Documents that left this source (deleted, or no longer matching), even
   * when another source still supplies them: a roster drops a member who
   * moved to another SubKrewe
   */
  left?: (paths: string[]) => void
  /** Called once the source is ready (a "loaded" flag) */
  ready?: () => void
  /** Called when the listener stops, after evict (a "loaded" flag goes back) */
  stopped?: () => void
  /** Listens to this whole collection, so sources `within` it can rely on it */
  covers?: string
  /** All documents of this source are in this collection */
  within?: string
}

export type LiveHolder = 'auto' | 'transient' | { session: string }

/** How long a source keeps listening after its last holder let go */
export const LIVE_LINGER_MS = 2 * 60 * 1000

/**
 * How long a source waits for the server after a snapshot from the cache
 * before it's ready with that (offline: what a getDoc would fall back to)
 */
export const LIVE_CACHE_GRACE_MS = 10 * 1000

interface Source {
  spec: LiveSpec
  holds: number
  ready: Promise<void>
  settled: boolean
  /** The documents this source supplies now */
  paths: Set<string>
  unsubscribe: (() => void) | null
  linger: ReturnType<typeof setTimeout> | null
  /** Waiting for the server after a snapshot from the cache */
  grace: ReturnType<typeof setTimeout> | null
  /** Snapshots are applied one at a time, in order */
  queue: Promise<void>
  /** Releases of the sources held on this one's behalf */
  dependents: (() => void)[]
}

const sources = new Map<string, Source>()
// Document path → keys of the sources that supply it
const owners = new Map<string, Set<string>>()
const sessions = new Map<string, (() => void)[]>()
// Null until the router plugin starts route holds (unit tests have none)
let routeReleases: (() => void)[] | null = null
// The source whose snapshot is being applied, while its apply runs
let applying: Source | null = null

function own(source: Source, path: string) {
  source.paths.add(path)
  const keys = owners.get(path)
  if (keys) keys.add(source.spec.key)
  else owners.set(path, new Set([source.spec.key]))
}

/** True when no source supplies the document any more */
function disown(source: Source, path: string): boolean {
  source.paths.delete(path)
  const keys = owners.get(path)
  if (!keys) return true
  keys.delete(source.spec.key)
  if (keys.size > 0) return false
  owners.delete(path)
  return true
}

function isRunning(source: Source): boolean {
  return sources.get(source.spec.key) === source
}

function stopSource(source: Source) {
  if (!isRunning(source)) return
  sources.delete(source.spec.key)
  if (source.linger) clearTimeout(source.linger)
  source.linger = null
  if (source.grace) clearTimeout(source.grace)
  source.grace = null
  try {
    source.unsubscribe?.()
  }
  catch (error) {
    console.error(`[LiveData] Error stopping ${source.spec.key}:`, error)
  }
  const orphaned = [...source.paths].filter(path => disown(source, path))
  if (orphaned.length > 0) source.spec.evict(orphaned)
  source.spec.stopped?.()
  for (const release of source.dependents.splice(0)) release()
}

function releaseSource(source: Source) {
  if (!isRunning(source)) return
  source.holds = Math.max(0, source.holds - 1)
  if (source.holds > 0 || source.linger) return
  source.linger = setTimeout(() => {
    source.linger = null
    if (source.holds === 0) stopSource(source)
  }, LIVE_LINGER_MS)
}

async function applyChanges(source: Source, changes: LiveChange[]) {
  if (!isRunning(source)) return
  const present = changes.filter(change => change.data !== null)
  const gone = changes.filter(change => change.data === null)
  for (const change of present) own(source, change.path)
  if (present.length > 0) {
    // Sources acquired while this snapshot is converted (a member's
    // SubKrewe) are held for as long as this source runs
    const previous = applying
    applying = source
    let result: void | Promise<void>
    try {
      result = source.spec.apply(present)
    }
    finally {
      applying = previous
    }
    await result
  }
  const leaving = gone.map(change => change.path).filter(path => source.paths.has(path))
  if (leaving.length > 0) source.spec.left?.(leaving)
  const orphaned = leaving.filter(path => disown(source, path))
  if (orphaned.length > 0) source.spec.evict(orphaned)
}

function startSource(spec: LiveSpec): Source {
  let resolve!: () => void
  let reject!: (error: unknown) => void
  const source: Source = {
    spec,
    holds: 0,
    ready: new Promise<void>((res, rej) => {
      resolve = res
      reject = rej
    }),
    settled: false,
    paths: new Set(),
    unsubscribe: null,
    linger: null,
    grace: null,
    queue: Promise.resolve(),
    dependents: [],
  }
  // A failure reaches whoever awaits ready; nobody awaiting isn't an error
  source.ready.catch(() => {})
  sources.set(spec.key, source)

  const settle = () => {
    if (source.grace) clearTimeout(source.grace)
    source.grace = null
    if (source.settled || !isRunning(source)) return
    source.settled = true
    spec.ready?.()
    resolve()
  }

  const fail = (error: unknown) => {
    console.error(`[LiveData] ${spec.key} failed:`, error)
    if (!source.settled) {
      source.settled = true
      reject(error)
    }
    // The next acquire starts it again
    stopSource(source)
  }

  try {
    source.unsubscribe = spec.listen((changes, fromCache = false) => {
      source.queue = source.queue
        .then(() => applyChanges(source, changes))
        .then(() => {
          if (!fromCache) settle()
          else if (!source.settled && !source.grace) source.grace = setTimeout(settle, LIVE_CACHE_GRACE_MS)
        })
        .catch(fail)
    }, fail)
  }
  catch (error) {
    fail(error)
  }
  return source
}

function hold(source: Source, holder: LiveHolder | 'manual'): () => void {
  source.holds++
  if (source.linger) {
    clearTimeout(source.linger)
    source.linger = null
  }
  let released = false
  const release = () => {
    if (released) return
    released = true
    releaseSource(source)
  }

  if (holder === 'manual') {
    // The caller lets go itself (holdLive)
  }
  else if (holder === 'transient') {
    source.ready.then(release, release)
  }
  else if (typeof holder === 'object') {
    const releases = sessions.get(holder.session)
    if (releases) releases.push(release)
    else sessions.set(holder.session, [release])
  }
  else if (getCurrentScope()) {
    onScopeDispose(release)
  }
  else if (applying && applying !== source) {
    applying.dependents.push(release)
  }
  else if (routeReleases) {
    routeReleases.push(release)
  }
  else {
    source.ready.then(release, release)
  }
  return release
}

function coveringSource(collection: string): Source | undefined {
  for (const source of sources.values()) {
    if (source.spec.covers === collection) return source
  }
  return undefined
}

/**
 * Hold a live source, starting its listener if nobody holds it yet.
 * Resolves once it's ready; rejects if the listener fails before that.
 */
export function acquireLive<T>(spec: LiveSpec<T>, holder: LiveHolder = 'auto'): Promise<void> {
  // Its own listener when that runs: the covering one may still be loading
  const covering = !sources.has(spec.key) && spec.within ? coveringSource(spec.within) : undefined
  if (covering) return covering.ready
  const source = sources.get(spec.key) ?? startSource(spec as LiveSpec)
  hold(source, holder)
  return source.ready
}

/**
 * Hold a live source until `release` is called, for a view that switches
 * between sources itself (a SubKrewe's dues for one year, then another).
 */
export function holdLive<T>(spec: LiveSpec<T>): { ready: Promise<void>, release: () => void } {
  const covering = !sources.has(spec.key) && spec.within ? coveringSource(spec.within) : undefined
  if (covering) return { ready: covering.ready, release: () => {} }
  const source = sources.get(spec.key) ?? startSource(spec as LiveSpec)
  return { ready: source.ready, release: hold(source, 'manual') }
}

/** Whether a source runs (ready or not) */
export function isLiveRunning(key: string): boolean {
  return sources.has(key)
}

/** Whether a source runs and is ready (a snapshot the server confirmed is in) */
export function isLiveReady(key: string): boolean {
  return sources.get(key)?.settled === true
}

/**
 * Resolves once every running source is ready or has failed, those started
 * meanwhile included
 */
export async function whenLiveSettled(): Promise<void> {
  for (;;) {
    const pending = [...sources.values()].filter(source => !source.settled)
    if (pending.length === 0) return
    await Promise.all(pending.map(source => source.ready.catch(() => {})))
  }
}

/** Whether a whole-collection source runs (ready or not) */
export function isLiveCovered(collection: string): boolean {
  return coveringSource(collection) !== undefined
}

/** Let go of everything held for a session group */
export function releaseLiveSession(group: string) {
  for (const release of sessions.get(group)?.splice(0) ?? []) release()
}

/**
 * A new route: what the previous one held is let go (it lingers). Called by
 * plugins/live-data.client.ts after each navigation.
 */
export function beginLiveRoute() {
  const previous = routeReleases
  routeReleases = []
  for (const release of previous ?? []) release()
}

/**
 * Stop one listener now, whoever holds it, and empty what only it supplied.
 * For a store that tears its own listener down (notifications, when they
 * are set up again for another member).
 */
export function stopLive(key: string) {
  const source = sources.get(key)
  if (source) stopSource(source)
}

/** Stop every listener and empty what they supplied (sign-out) */
export function resetLive() {
  for (const source of [...sources.values()]) stopSource(source)
  sessions.clear()
  owners.clear()
  if (routeReleases) routeReleases = []
  applying = null
}

/** For unit tests: also forget that the router plugin ran */
export function resetLiveForTests() {
  resetLive()
  routeReleases = null
}

/** What's live right now, for debugging */
export function liveDiagnostics() {
  return [...sources.values()].map(source => ({
    key: source.spec.key,
    holds: source.holds,
    documents: source.paths.size,
    ready: source.settled,
    lingering: source.linger !== null,
    dependents: source.dependents.length,
  }))
}

interface SnapshotDoc { id: string, ref?: { path?: string }, data: () => unknown }

interface SnapshotLike {
  docChanges?: () => { type: string, doc: SnapshotDoc }[]
  docs?: SnapshotDoc[]
  forEach?: (callback: (doc: SnapshotDoc) => void) => void
}

/**
 * The changes in a query snapshot. Removed documents (deleted, or no longer
 * matching) come through with data null.
 */
export function liveQueryChanges<T>(snapshot: SnapshotLike, collectionPath: string): LiveChange<T>[] {
  const pathOf = (doc: SnapshotDoc) => doc.ref?.path ?? `${collectionPath}/${doc.id}`
  if (typeof snapshot.docChanges === 'function') {
    return snapshot.docChanges().map(change => ({
      path: pathOf(change.doc),
      data: change.type === 'removed' ? null : change.doc.data() as T,
    }))
  }
  // Test doubles: a list of documents, all present
  const docs: SnapshotDoc[] = snapshot.docs ?? []
  if (!snapshot.docs && typeof snapshot.forEach === 'function') snapshot.forEach(doc => docs.push(doc))
  return docs.map(doc => ({ path: pathOf(doc), data: doc.data() as T }))
}

/** The change in a document snapshot: its data, or null when it doesn't exist */
export function liveDocChange<T>(snapshot: { exists: () => boolean, data: () => unknown }, path: string): LiveChange<T>[] {
  return [{ path, data: snapshot.exists() ? snapshot.data() as T : null }]
}

const fromCacheOnly = (snapshot: { metadata?: { fromCache?: boolean } }) => snapshot.metadata?.fromCache === true

// Metadata changes too: a document Firestore had cached comes from the cache
// first, and only a metadata change says the server confirmed it
const LISTEN_OPTIONS = { includeMetadataChanges: true } as const

/**
 * The rest of a LiveSpec's onSnapshot on one document, after the reference:
 * each snapshot goes to emit, marked when it came from the cache alone.
 *
 *   listen: (emit, fail) => onSnapshot(doc(db, 'members', id), ...liveDocListener(path, emit, fail))
 */
export function liveDocListener<T>(path: string, emit: LiveEmit<T>, fail: (error: unknown) => void) {
  return [LISTEN_OPTIONS, (snapshot: DocumentSnapshot) => emit(liveDocChange<T>(snapshot, path), fromCacheOnly(snapshot)), fail] as const
}

/** The rest of a LiveSpec's onSnapshot on a query or collection, as liveDocListener */
export function liveQueryListener<T>(collectionPath: string, emit: LiveEmit<T>, fail: (error: unknown) => void) {
  return [LISTEN_OPTIONS, (snapshot: QuerySnapshot) => emit(liveQueryChanges<T>(snapshot, collectionPath), fromCacheOnly(snapshot)), fail] as const
}
